// Núcleo del diseñador visual ZPL — port fiel del configurador de boletas de ARMENTO
// (bagger_labels.html, funciones dz*). Son funciones puras sobre strings ZPL:
// parseo en segmentos raw/bloque, reescritura DIRIGIDA (solo lo tocado cambia) y
// estimación de tamaño de bloques para la capa de arrastre.

// Campos que rellena el controller del MES (tasks/labels.py, format_map sobre
// labels.data_general + inyectados id/date/fgs/qr_code_data/qr_00N).
// OJO: {client}/{count} solo existen en la etiqueta FGS extra (código) y
// {especial} es legacy — en plantillas de boleta imprimirían vacío.
export const ZPL_FIELD_OPTIONS = [
    { value: '{patient}', label: 'Patient', group: 'Case information' },
    { value: '{doctor}', label: 'Doctor', group: 'Case information' },
    { value: '{id}', label: 'Order ID', group: 'Case information' },
    { value: '{ref}', label: 'Reference / REF', group: 'Case information' },
    { value: '{lot}', label: 'LOT', group: 'Case information' },
    { value: '{type}', label: 'Product type', group: 'Product' },
    { value: '{upper}', label: 'Upper steps', group: 'Product' },
    { value: '{lower}', label: 'Lower steps', group: 'Product' },
    { value: '{date}', label: 'Expiration date', group: 'Product' },
    { value: '{fgs}', label: 'FGS', group: 'Product' },
    { value: '{manufacturer}', label: 'Manufacturer', group: 'Regulatory' },
    { value: '{address}', label: 'Manufacturer address', group: 'Regulatory' },
    { value: '{qr_code_data_text}', label: 'QR data text', group: 'Advanced QR text' },
    { value: '{qr_001}', label: 'QR segment 1', group: 'Advanced QR text' },
    { value: '{qr_002}', label: 'QR segment 2', group: 'Advanced QR text' },
    { value: '{qr_003}', label: 'QR segment 3', group: 'Advanced QR text' },
    { value: '{qr_004}', label: 'QR segment 4', group: 'Advanced QR text' },
    { value: '{qr_005}', label: 'QR segment 5', group: 'Advanced QR text' },
    { value: '{qr_006}', label: 'QR segment 6', group: 'Advanced QR text' }
];

// qr_code_data is intentionally available to previews/blanking but is inserted
// only through the protected QR palette item, never as an editable text field.
export const ZPL_FIELDS = [
    ...ZPL_FIELD_OPTIONS.map(field => field.value),
    '{qr_code_data}'
];

// El texto del usuario nunca debe traer '^' (control de ZPL) ni saltos de línea
export const zplClean = (s) => String(s == null ? '' : s).replace(/[\^\r\n]/g, ' ');

export const zplParseBlock = (raw, id) => {
    const b = { kind: 'block', id: 'b' + id, raw, dirty: false };
    const fo = raw.match(/\^F([OT])(\d+),(\d+)/);
    b.baseline = !!(fo && fo[1] === 'T');
    b.x = fo ? +fo[2] : 0;
    b.y = fo ? +fo[3] : 0;
    const a = raw.match(/\^A[0@][NRIB]?,(\d+)(?:,(\d+))?/);
    b.fh = a ? +a[1] : 30;
    b.fw = (a && a[2]) ? +a[2] : null;
    if (/\^BQ/.test(raw)){
        b.type = 'qr';
        const q = raw.match(/\^BQ[^,]*,\d+,(\d+)/); b.mag = q ? +q[1] : 4;
        const fd = raw.match(/\^F[DV]([^^]*)/); b.qdata = fd ? fd[1] : '';
    } else if (/\^BX/.test(raw)){
        b.type = 'datamatrix';
        const dx = raw.match(/\^BX[^,]*,(\d+)/); b.dmmod = dx ? +dx[1] : 4;
        const fd = raw.match(/\^F[DV]([^^]*)/); b.qdata = fd ? fd[1] : '';
    } else if (/\^B[C3]/.test(raw)){
        b.type = 'barcode';
        const fd = raw.match(/\^F[DV]([^^]*)/); b.text = fd ? fd[1] : '';
    } else if (/\^GFA/.test(raw)){
        b.type = 'image';
        const g = raw.match(/\^GFA,(\d+),\d+,(\d+),/);
        if (g){ b.bpr = +g[2]; b.iw = b.bpr * 8; b.ih = Math.round((+g[1]) / (b.bpr || 1)); }
    } else if (/\^GB/.test(raw)){
        b.type = 'box';
        const gb = raw.match(/\^GB(\d+),(\d+),(\d+)/);
        if (gb){ b.bw = +gb[1]; b.bh = +gb[2]; b.bt = +gb[3]; }
    } else if (/\^GD/.test(raw)){
        b.type = 'line';
    } else if (/\^F[DV]/.test(raw)){
        const fd = raw.match(/\^F[DV]([^^]*)/); const txt = fd ? fd[1] : '';
        b.text = txt; b.type = /\{[a-z_0-9]+\}/i.test(txt) ? 'field' : 'text';
    } else b.type = 'other';
    return b;
};

export const zplParse = (zpl) => {
    const segs = [];
    const re = /\^F[OT][\s\S]*?\^FS/g;
    let last = 0, m, id = 0;
    while ((m = re.exec(zpl)) !== null){
        if (m.index > last) segs.push({ kind: 'raw', text: zpl.slice(last, m.index) });
        segs.push(zplParseBlock(m[0], id++));
        last = m.index + m[0].length;
    }
    if (last < zpl.length) segs.push({ kind: 'raw', text: zpl.slice(last) });
    return segs;
};

// ZPL de un bloque: reescritura DIRIGIDA sobre el raw (solo lo tocado cambia)
export const zplBlockZpl = (b) => {
    let s = b.raw;
    s = s.replace(/(\^F[OT])\d+,\d+/, '$1' + Math.round(b.x) + ',' + Math.round(b.y));
    if (b.fh != null && /\^A[0@]/.test(s)){
        s = s.replace(/(\^A[0@][NRIB]?,)\d+(,\d+)?/, function(mm, p1, p2){
            return p1 + Math.round(b.fh) + (b.fw != null ? ',' + Math.round(b.fw) : (p2 || '')); });
    }
    // Los textos del usuario se insertan con forma de FUNCIÓN para que un '$'
    // en el dato (p.ej. "$100") no se interprete como referencia de captura.
    if ((b.type === 'text' || b.type === 'field') && b.text != null)
        s = s.replace(/(\^F[DV])[^^]*/, function(mm, p1){ return p1 + zplClean(b.text); });
    if (b.type === 'box' && b.bw != null)
        s = s.replace(/(\^GB)\d+,\d+,\d+/, '$1' + Math.round(b.bw) + ',' + Math.round(b.bh) + ',' + Math.round(b.bt));
    if (b.type === 'qr'){
        if (b.mag != null) s = s.replace(/(\^BQ[^,]*,\d+,)\d+/, '$1' + Math.round(b.mag));
        if (b.qdata != null) s = s.replace(/(\^F[DV])[^^]*/, function(mm, p1){ return p1 + zplClean(b.qdata); });
    }
    if (b.type === 'datamatrix' && b.qdata != null)
        s = s.replace(/(\^F[DV])[^^]*/, function(mm, p1){ return p1 + zplClean(b.qdata); });
    return s;
};

export const zplGen = (segs) => segs.map(
    (s) => s.kind === 'raw' ? s.text : (s.dirty ? zplBlockZpl(s) : s.raw)
).join('');

// dimensiones aproximadas del bloque (dots) para la capa de arrastre
let _measCtx = null;
const measCtx = () => {
    if (!_measCtx){
        _measCtx = document.createElement('canvas').getContext('2d');
    }
    return _measCtx;
};

export const zplBlockSize = (b) => {
    if (b.type === 'box') return [Math.max(6, b.bw || 20), Math.max(6, b.bh || 20)];
    if (b.type === 'image') return [Math.max(8, b.iw || 60), Math.max(8, b.ih || 40)];
    if (b.type === 'qr'){ const s = Math.max(20, (b.mag || 4) * 30); return [s, s]; }
    if (b.type === 'datamatrix'){ const s = Math.max(20, (b.dmmod || 4) * 24); return [s, s]; }
    if (b.type === 'line') return [Math.max(10, b.fh || 12), Math.max(6, b.fh || 12)];
    const disp = (b.text || '') || '{campo}';
    const ctx = measCtx();
    ctx.font = Math.round((b.fh || 24) * 0.81) + 'px "DejaVu Sans", Arial, sans-serif';
    const w = Math.max(14, ctx.measureText(disp).width);
    const h = (b.type === 'barcode') ? Math.max(30, b.fh || 40) : (b.fh || 24);
    return [Math.ceil(w), Math.ceil(h)];
};

export const zplBlockLabel = (b) => {
    if (b.type === 'text') return 'T: ' + (b.text || '').trim().slice(0, 22);
    if (b.type === 'field'){
        const field = ZPL_FIELD_OPTIONS.find(item => item.value === (b.text || '').trim());
        return field ? field.label : (b.text || '').trim().slice(0, 22);
    }
    if (b.type === 'box') return 'Caja';
    if (b.type === 'line') return 'Línea';
    if (b.type === 'qr') return 'QR';
    if (b.type === 'datamatrix') return 'DataMatrix';
    if (b.type === 'barcode') return 'Barras';
    if (b.type === 'image') return 'Imagen';
    return '·';
};

export const zplBlankData = () => {
    const d = {};
    ZPL_FIELDS.forEach((f) => { d[f.replace(/[{}]/g, '')] = ''; });
    return d;
};
